import { useSyncExternalStore } from 'react';

export const CART_TYPES = ['seminar_376', 'muenchenprogramm'] as const;

export type CartType = (typeof CART_TYPES)[number];

export const CART_PATHS: Record<CartType, string> = {
  seminar_376: '/seminare/cart',
  muenchenprogramm: '/muenchenprogramm/cart',
};

const CART_STORAGE_KEYS: Record<CartType, string> = {
  seminar_376: 'dgbbw_cart_seminare',
  muenchenprogramm: 'dgbbw_cart_muenchenprogramm',
};

export type CartItem = {
  seminarId: string;
  quantity: number;
  courseNumber?: string;
  topic?: string;
  startDate?: string;
  endDate?: string;
  price?: number;
};

export type Cart = {
  version: 2;
  items: CartItem[];
};

const emptyCart: Cart = {
  version: 2,
  items: [],
};

const carts: Record<CartType, Cart> = {
  seminar_376: emptyCart,
  muenchenprogramm: emptyCart,
};

let initialized = false;
const listeners = new Set<() => void>();

export const isCartType = (value?: string): value is CartType =>
  CART_TYPES.some((cartType) => cartType === value);

export const getCartTypeFromPathname = (pathname: string): CartType | null => {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';

  return (
    CART_TYPES.find(
      (cartType) => CART_PATHS[cartType] === normalizedPathname,
    ) || null
  );
};

export const parseStoredCart = (value: string | null): Cart => {
  if (!value) return emptyCart;

  try {
    const parsed = JSON.parse(value) as Partial<Cart>;

    if (parsed.version !== 2 || !Array.isArray(parsed.items)) {
      return emptyCart;
    }

    const seminarIds = new Set<string>();
    const items = parsed.items.reduce<CartItem[]>((validItems, item) => {
      const isValid =
        typeof item?.seminarId === 'string' &&
        item.seminarId.length > 0 &&
        !seminarIds.has(item.seminarId);

      if (isValid) {
        seminarIds.add(item.seminarId);
        validItems.push({
          ...item,
          quantity:
            Number.isInteger(item.quantity) &&
            item.quantity >= 1 &&
            item.quantity <= 4
              ? item.quantity
              : 1,
        });
      }

      return validItems;
    }, []);

    return {
      version: 2,
      items,
    };
  } catch {
    return emptyCart;
  }
};

const notify = () => listeners.forEach((listener) => listener());

const saveCart = (cartType: CartType, nextCart: Cart) => {
  try {
    window.localStorage.setItem(
      CART_STORAGE_KEYS[cartType],
      JSON.stringify(nextCart),
    );
  } catch {
    return false;
  }

  carts[cartType] = nextCart;
  notify();
  return true;
};

const handleStorage = (event: StorageEvent) => {
  const cartType = CART_TYPES.find(
    (candidate) => CART_STORAGE_KEYS[candidate] === event.key,
  );

  if (!cartType) return;

  carts[cartType] = parseStoredCart(event.newValue);
  notify();
};

const initialize = () => {
  if (initialized || typeof window === 'undefined') return;

  initialized = true;
  CART_TYPES.forEach((cartType) => {
    carts[cartType] = parseStoredCart(
      window.localStorage.getItem(CART_STORAGE_KEYS[cartType]),
    );
  });
  window.addEventListener('storage', handleStorage);
};

const subscribe = (listener: () => void) => {
  initialize();
  listeners.add(listener);

  return () => listeners.delete(listener);
};

const getSnapshot = (cartType: CartType | null) => {
  initialize();
  return cartType ? carts[cartType] : emptyCart;
};

const getServerSnapshot = () => emptyCart;

export const useCart = (cartType: CartType | null) =>
  useSyncExternalStore(
    subscribe,
    () => getSnapshot(cartType),
    getServerSnapshot,
  );

export const addCartItem = (
  cartType: CartType,
  seminarId: string,
  details?: Omit<CartItem, 'seminarId' | 'quantity'>,
) => {
  initialize();
  const cart = carts[cartType];

  if (!seminarId || cart.items.some((item) => item.seminarId === seminarId)) {
    return false;
  }

  return saveCart(cartType, {
    version: 2,
    items: [
      ...cart.items,
      {
        seminarId,
        quantity: 1,
        ...details,
      },
    ],
  });
};

export const updateCartItemQuantity = (
  cartType: CartType,
  seminarId: string,
  quantity: number,
) => {
  initialize();
  const cart = carts[cartType];

  if (
    !seminarId ||
    !Number.isInteger(quantity) ||
    quantity < 1 ||
    quantity > 4 ||
    !cart.items.some((item) => item.seminarId === seminarId)
  ) {
    return false;
  }

  return saveCart(cartType, {
    version: 2,
    items: cart.items.map((item) =>
      item.seminarId === seminarId ? { ...item, quantity } : item,
    ),
  });
};

export const removeCartItem = (cartType: CartType, seminarId: string) => {
  initialize();
  const cart = carts[cartType];

  if (!seminarId || !cart.items.some((item) => item.seminarId === seminarId)) {
    return false;
  }

  return saveCart(cartType, {
    version: 2,
    items: cart.items.filter((item) => item.seminarId !== seminarId),
  });
};

export const clearCart = (cartType: CartType) => {
  initialize();
  return saveCart(cartType, emptyCart);
};

export const getCartBookingCount = (cart: Cart) =>
  cart.items.reduce((total, item) => total + item.quantity, 0);

export const getCartTotal = (cart: Cart) =>
  cart.items.reduce(
    (total, item) =>
      typeof item.price === 'number'
        ? total + item.price * item.quantity
        : total,
    0,
  );

export const formatCartPrice = (value?: number) =>
  typeof value === 'number'
    ? `${value.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`
    : '–';

export const formatCartDate = (value?: string) => {
  if (!value) return '–';

  const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? '–'
    : date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
};

const formatCartText = (value?: string) => value || '–';

export const serializeCartForSubmission = (cart: Cart) => {
  const courses = cart.items.map((item, index) =>
    [
      `Kurs ${index + 1}`,
      `Kursnummer: ${formatCartText(item.courseNumber)}`,
      `Thema: ${formatCartText(item.topic)}`,
      `Termin Beginn: ${formatCartDate(item.startDate)}`,
      `Termin Ende: ${formatCartDate(item.endDate)}`,
      `Anzahl: ${item.quantity}`,
      `Preis: ${formatCartPrice(item.price)}`,
    ].join('\n'),
  );

  return [
    ...(courses.length ? courses : ['Der Warenkorb ist leer.']),
    `Warenkorbpreis: ${formatCartPrice(getCartTotal(cart))}`,
  ].join('\n\n');
};
